import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useVerificationInfo } from '../api/queries/useVerificationInfo';
import { Loader, ModalComponentWrapper } from '@/shared/components';
import { VerificationEulaProfileForm } from './VerificationEulaProfileForm';
import { type VerificationStepId } from '../api/types';
import { VerificationQuestionariesForm } from './VerificationQuestionariesForm';
import { VerificationDocumentsCheck } from './VerificationDocumentsCheck';
import { VERIFICATION_STEPS } from '@/shared/config';
import type { EulaProfileFormValues } from '@/features/user-verification/model';
import { VerificationCta } from '@/widgets';
import { useSessionStore } from '@/entities/session';
import { cn } from '@/shared/utils';
import { tv } from 'tailwind-variants';

const classes = tv({
  slots: {
    defaultContainer: 'min-h-[43rem]',
  },
  variants: {
    isCorporateSuccess: {
      true: {
        defaultContainer: 'max-h-[43rem]',
      },
    },
  },
});

type Props = {
  goBack: () => void;
  onClose: () => void;
  initialValues?: EulaProfileFormValues;
};

const Verification = ({
  goBack,
  onClose: closeDialog,
  initialValues,
}: Props) => {
  const queryClient = useQueryClient();
  const [activeStepId, setActiveStepId] = useState<VerificationStepId | null>(
    null
  );

  const { session } = useSessionStore();
  const eWallet = session?.additional?.eWallet || '';
  const isCorporate = session?.additional?.isCorporate || false;

  const { userVerificationInfo, isVerificationInfoLoading } =
    useVerificationInfo();

  useEffect(() => {
    if (userVerificationInfo?.stepId === VERIFICATION_STEPS.COMPLIANCE_CHECK) {
      closeDialog();
      return;
    }

    setActiveStepId(userVerificationInfo?.stepId || null);
  }, [userVerificationInfo, queryClient]);

  const activeStepComponent = useMemo(() => {
    if (activeStepId) {
      switch (activeStepId) {
        case 'eula':
        case 'profile':
          return (
            <VerificationEulaProfileForm
              initialValues={initialValues}
              goBack={goBack}
            />
          );
        case 'questionaries':
          return <VerificationQuestionariesForm />;
        case 'sumsub':
          return <VerificationDocumentsCheck />;
        default:
          if (activeStepId === 'finish' && isCorporate)
            return (
              <VerificationCta ewalletAccNum={eWallet} className="h-full" />
            );
          return <>Unknown step {activeStepId}</>;
      }
    }
    return null;
  }, [activeStepId, isCorporate, eWallet]);

  const { defaultContainer } = classes({
    isCorporateSuccess: isCorporate && activeStepId === 'finish',
  });

  if (activeStepId === 'sumsub') {
    return (
      <ModalComponentWrapper className={cn(defaultContainer())}>
        {isVerificationInfoLoading ? <Loader /> : activeStepComponent}
      </ModalComponentWrapper>
    );
  }

  return (
    <div className={cn(defaultContainer())}>
      {isVerificationInfoLoading ? <Loader /> : activeStepComponent}
    </div>
  );
};

export { Verification };
